import React, { FC } from 'react';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { AxiosError } from 'axios';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { useTranslation } from 'react-i18next';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { fundTransfersService } from '../../../../../../channels/secure-web/services';
import { ExchangeCalculateResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { ConsolidatedTransactionDetails } from '../../../domain/Transaction/entities/ConsolidatedTransaction';
import { GenericDrawer } from '../../../../../content-composer/src/components/Generic/GenericDrawer';
import { useOnTransactionDetailsMutation } from '../../../../../authorizations/src';
import { AccountExtraitTransaction } from '../../../domain/Transaction/entities/AccountExtraitTransaction';
import { Mandate } from '../../../../../authorizations/src/domain/Authorization/entities';
import { AccountExtraitTransactions } from '../../../domain/Transaction/entities/AccountExtraitTransaction/AccountExtraitTransactionResponseDTO';
import { AccountExtraitTransactionsPrint } from '../../../domain/Transaction/entities/AccountExtraitTransaction/AccountExtraitTransactionsPrintResponseDTO';
import { exchangeAmountPredicator } from '../../../utils';
import { TransactionDetailsContent } from './TransactionDetailsContent';
import { TransactionDetailsDrawerFileDownload } from './TransactionDetailsDrawerFileDownload';

export interface TransactionDetailsDrawerProps {
  isOpen: boolean;
  onToggleDrawer: (transactionId: any) => void;
  isAuthorization?: boolean;
  isLoading: boolean;
  error:
    | AxiosError<
        APIGatewayResponse<
          ConsolidatedTransactionDetails | AccountExtraitTransactions | AccountExtraitTransactionsPrint | unknown
        >,
        any
      >
    | undefined;
  transaction?: Nullable<AccountExtraitTransaction> | Nullable<Mandate>;
  transactionDetails: Nullable<ConsolidatedTransactionDetails>;
  lastAuthorizer: boolean;
  needMandate: boolean;
  shouldShowChildren?: boolean;
  actionCallback?: (event: any, name: string, transactionNo: string, isActionFromDetailsDrawer: boolean) => void;
  shouldFetchDealerRates?: boolean;
  shouldShowMandateRequestRateButton?: boolean;
  shouldShowApproximateRate?: boolean;
}

const TransactionDetailsDrawer: FC<TransactionDetailsDrawerProps> = ({
  isOpen,
  onToggleDrawer,
  isAuthorization,
  isLoading,
  error,
  transaction,
  transactionDetails,
  lastAuthorizer,
  shouldShowChildren,
  actionCallback,
  shouldFetchDealerRates = false,
  needMandate = true,
  shouldShowMandateRequestRateButton = false,
  shouldShowApproximateRate,
  children,
}) => {
  const { t } = useTranslation();

  const onTransactionDetailsMutation = useOnTransactionDetailsMutation();

  //bank employer has accepted in core system the requested dealer rate
  const doesUserAwaitsForDealerRateAcceptance = Boolean(
    transactionDetails &&
      transactionDetails.processingStatus === '6' &&
      !transactionDetails.dealRefNo &&
      !transactionDetails.dealUsrCode,
  );

  const shouldMakeExchangeAmountCall =
    !!transactionDetails &&
    ((exchangeAmountPredicator(transactionDetails?.processingStatus) &&
      (transactionDetails?.fromCurrencyId !== transactionDetails?.toCurrencyId ||
        (transactionDetails?.internalDestinationCurrencyId !== 0 &&
          transactionDetails?.internalDestinationCurrencyId !== transactionDetails?.fromCurrencyId))) ||
      doesUserAwaitsForDealerRateAcceptance);

  const {
    data: exchangeCalculateData,
    error: exchangeCalculateError,
    isLoading: isLoadingExchangeCalculate,
  } = useInvokeSWRService<ExchangeCalculateResponseBody>(
    fundTransfersService.ExchangeCalculate,
    {
      fromCurrencyId: transactionDetails?.fromCurrencyId,
      toCurrencyId:
        transactionDetails?.internalDestinationCurrencyId !== 0 &&
        transactionDetails?.internalDestinationCurrencyId !== transactionDetails?.fromCurrencyId
          ? transactionDetails?.internalDestinationCurrencyId
          : transactionDetails?.toCurrencyId,
      amount: transactionDetails?.amount,
      isSideTypeBuy: transactionDetails?.accountCurrency === transactionDetails?.exchangeCurrency,
    },
    shouldMakeExchangeAmountCall,
    'fundTransfersService.ExchangeCalculate',
  );

  const errors = error || exchangeCalculateError;

  const isLoadingData = isLoading || isLoadingExchangeCalculate;

  return (
    <GenericDrawer
      anchor='bottom'
      title={t('transactionDetailsDrawer.Title')}
      isOpen={isOpen}
      onToggleDrawer={onToggleDrawer}
      DownloadTransactionDetailsComponent={() => (
        <TransactionDetailsDrawerFileDownload
          isLoading={isLoadingData}
          isAuthorization={isAuthorization}
          transactionDetails={transactionDetails}
        />
      )}>
      {!isEmpty(errors) && (
        <ErrorContent
          code={errors?.code || '404'}
          onClickBtn={onTransactionDetailsMutation}
          subtitle={errors?.message}
          btnLabel={
            transaction
              ? t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')
              : t('consolidatedTransactionDetailsPage.ErrorContent.BtnLabel')
          }
          message={
            transaction
              ? t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')
              : t('consolidatedTransactionDetailsPage.ErrorContent.Message')
          }
        />
      )}
      {!shouldShowChildren && isEmpty(errors) && (
        <TransactionDetailsContent
          variant={'drawer'}
          doesUserAwaitsForDealerRateAcceptance={doesUserAwaitsForDealerRateAcceptance}
          transaction={transaction as Mandate | AccountExtraitTransaction}
          transactionDetails={transactionDetails as ConsolidatedTransactionDetails}
          isAuthorization={isAuthorization}
          onToggleDrawer={onToggleDrawer}
          isLoading={isLoadingData}
          lastAuthorizer={lastAuthorizer}
          needMandate={needMandate}
          actionCallback={actionCallback}
          shouldFetchDealerRates={shouldFetchDealerRates}
          exchangeCalculateData={exchangeCalculateData}
          shouldShowMandateRequestRateButton={shouldShowMandateRequestRateButton}
          shouldShowApproximateRate={shouldShowApproximateRate}
        />
      )}
      {shouldShowChildren && children}
    </GenericDrawer>
  );
};

export { TransactionDetailsDrawer };
